import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { timeAgo, apiError } from "@/lib/api";
import { PageHeader } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const NONE = "__auto__";

const hoursLeft = (iso: string) =>
  Math.max(0, (new Date(iso).getTime() - Date.now()) / 3.6e6).toFixed(1);

const ART_SLOTS = [
  { key: "flagship", label: "Flagship — vault artwork", fallback: "/originals/vault-hero.png" },
  { key: "support-1", label: "Support 1 thumbnail", fallback: "/originals/support-1.png" },
  { key: "support-2", label: "Support 2 thumbnail", fallback: "/originals/support-2.png" },
  { key: "support-3", label: "Support 3 thumbnail", fallback: "/originals/support-3.png" },
];

const OriginalsArtworkPanel = () => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const { data } = useQuery({
    queryKey: ["admin-originals-art"],
    queryFn: async () => (await api.get("/admin/originals/artwork")).data,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-originals-art"] });
    qc.invalidateQueries({ queryKey: ["originals-artwork"] });
  };
  const upload = async (slot: string, file: File) => {
    setBusy(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/originals/artwork/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Artwork updated — live on the homepage");
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const reset = async (slot: string) => {
    try {
      await api.delete(`/admin/originals/artwork/${slot}`);
      toast.success("Reverted to the built-in approved artwork");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const slots = data?.slots || {};
  return (
    <div className="bg-[#121212] border border-white/10 rounded-lg p-5" data-testid="originals-artwork-panel">
      <Label className="text-white">NewsLayer Originals — artwork</Label>
      <p className="text-gray-500 text-xs mt-1 mb-4">
        Swap the flagship and supporting images without a code change. Images only, up to 5MB.
        Reset returns a slot to the built-in approved artwork.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {ART_SLOTS.map((s) => (
          <div key={s.key} className="bg-[#1A1A1A] border border-white/[0.06] rounded-md p-3"
            data-testid={`originals-art-slot-${s.key}`}>
            <img src={slots[s.key] || s.fallback} alt="" className="w-full h-24 object-cover rounded" />
            <p className="text-[11px] text-gray-300 mt-2">{s.label}</p>
            <p className="font-mono text-[10px] text-gray-600">{slots[s.key] ? "custom upload" : "built-in default"}</p>
            <div className="flex gap-2 mt-2">
              <label className="h-7 px-2 inline-flex items-center rounded-md text-[11px] cursor-pointer border border-white/15 text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                {busy === s.key ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" className="hidden" disabled={busy === s.key}
                  data-testid={`originals-art-upload-${s.key}`}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(s.key, f); e.target.value = ""; }} />
              </label>
              {slots[s.key] && (
                <Button size="sm" data-testid={`originals-art-reset-${s.key}`}
                  className="h-7 px-2 text-[11px] bg-transparent border border-white/15 text-red-400 hover:bg-white/10 hover:text-red-300"
                  onClick={() => reset(s.key)}>
                  Reset
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BreakingPanel = ({ recent }: { recent: any[] }) => {
  const qc = useQueryClient();
  const [addId, setAddId] = useState("");
  const { data } = useQuery({
    queryKey: ["admin-breaking"],
    queryFn: async () => (await api.get("/admin/breaking-carousel")).data,
    refetchInterval: 60000,
  });
  const act = async (fn: () => Promise<any>, msg: string) => {
    try {
      await fn();
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ["admin-breaking"] });
      qc.invalidateQueries({ queryKey: ["homepage"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const entries = data?.entries || [];
  const btn = "h-7 px-2 text-[11px] bg-transparent border border-white/15 text-gray-300 hover:bg-white/10 hover:text-white";
  return (
    <div className="bg-[#121212] border border-white/10 rounded-lg p-5" data-testid="breaking-carousel-panel">
      <Label className="text-white">Breaking carousel — active slides ({entries.length}/6)</Label>
      <p className="text-gray-500 text-xs mt-1 mb-3">
        Auto-managed: ~2-4h residency per story (2h soft / 4h hard), candidates from the last 6h,
        normal target 3, max 6 during heavy news cycles. Pinned slides never auto-expire.
      </p>
      {entries.length === 0 && (
        <p className="text-gray-500 text-sm mb-3">No active breaking slides — the homepage falls back to Top Story.</p>
      )}
      <div className="space-y-2">
        {entries.map((e: any, i: number) => (
          <div key={e.id} data-testid={`breaking-entry-${i}`}
            className="flex items-center gap-3 bg-[#1A1A1A] border border-white/[0.06] rounded-md px-3 py-2">
            <span className="font-mono text-[10px] text-gray-600">{String(i + 1).padStart(2, "0")}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white truncate">{e.title}</p>
              <p className="font-mono text-[10px] text-gray-500">
                {e.age_hours}h in carousel · {e.pinned ? "PINNED" : `expires in ${hoursLeft(e.expires_at)}h`} · {e.added_by}
              </p>
            </div>
            <Button size="sm" className={btn} data-testid={`breaking-pin-${i}`}
              onClick={() => act(() => api.patch(`/admin/breaking-carousel/${e.id}`, { pinned: !e.pinned }),
                e.pinned ? "Unpinned" : "Pinned")}>
              {e.pinned ? "Unpin" : "Pin"}
            </Button>
            <Button size="sm" className={btn} data-testid={`breaking-extend-${i}`}
              onClick={() => act(() => api.patch(`/admin/breaking-carousel/${e.id}`, { extend_hours: 4 }), "Extended +4h")}>
              +4h
            </Button>
            <Button size="sm" className={`${btn} text-red-400 hover:text-red-300`} data-testid={`breaking-remove-${i}`}
              onClick={() => act(() => api.delete(`/admin/breaking-carousel/${e.id}`), "Removed from carousel")}>
              Remove
            </Button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-4">
        <Select value={addId} onValueChange={setAddId}>
          <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white flex-1" data-testid="breaking-add-select">
            <SelectValue placeholder="Add a story to the carousel…" /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10 text-white max-h-[300px]">
            {recent.map((a: any) => (
              <SelectItem key={a.id} value={a.id}>{a.title.slice(0, 70)} · {timeAgo(a.published_at)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" className="bg-[#7C3AED] text-white hover:bg-[#6D28D9]" data-testid="breaking-add-button"
          disabled={!addId}
          onClick={() => act(() => api.post("/admin/breaking-carousel", { article_id: addId }), "Added to carousel")}>
          Add
        </Button>
      </div>
    </div>
  );
};

export default function HomepageManager() {
  const queryClient = useQueryClient();
  const [hero, setHero] = useState(NONE);
  const [order, setOrder] = useState("");

  const { data } = useQuery({
    queryKey: ["admin-homepage"],
    queryFn: async () => (await api.get("/admin/homepage")).data,
  });

  useEffect(() => {
    if (data?.config) {
      setHero(data.config.hero_article_id || NONE);
      setOrder((data.config.category_order || []).join(", "));
    }
  }, [data]);

  const save = async () => {
    try {
      await api.patch("/admin/homepage", {
        hero_article_id: hero === NONE ? null : hero,
        category_order: order.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Homepage configuration saved");
      queryClient.invalidateQueries({ queryKey: ["homepage"] });
      queryClient.invalidateQueries({ queryKey: ["admin-homepage"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-homepage-page">
      <PageHeader title="Homepage Manager" sub="Control the hero slot and category block ordering" />
      <div className="max-w-2xl space-y-6">
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <Label className="text-white">Hero story</Label>
          <p className="text-gray-500 text-xs mt-1 mb-3">
            "Automatic" uses the most recent featured story, falling back to the newest published story.
          </p>
          <Select value={hero} onValueChange={setHero}>
            <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white" data-testid="homepage-hero-select">
              <SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white max-h-[300px]">
              <SelectItem value={NONE}>Automatic (featured / latest)</SelectItem>
              {(data?.recent_articles || []).map((a: any) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.title.slice(0, 70)} · {timeAgo(a.published_at)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <Label className="text-white">Category block order</Label>
          <p className="text-gray-500 text-xs mt-1 mb-3">
            Comma-separated category slugs, shown top to bottom (max 4 rendered):
            web3, defi, blockchain, crypto, government-policy, markets, explainers, research
          </p>
          <Input value={order} onChange={(e) => setOrder(e.target.value)} data-testid="homepage-order-input"
            className="bg-[#1A1A1A] border-white/10 text-white font-mono text-sm" />
        </div>
        <Button onClick={save} data-testid="homepage-save-button"
          className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">Save homepage config</Button>
        <BreakingPanel recent={data?.recent_articles || []} />
        <OriginalsArtworkPanel />
      </div>
    </div>
  );
}
